import Protected from "@/pages/protected";
import { DataService } from "@/services/data";
import { handle_error, handle_success } from "@/utils/wretch";
import { json, Outlet, RouteObject } from "react-router-dom";

type CampaignPicklistOption = { value: string; label: string };
type WrapupApiItem = { id: number; name: string; status?: string };

const LeadRecyclingRoute: RouteObject = {
    path: "recyclage-leads",
    element: (
        <Protected profiles={["sales_admin", "system_admin"]}>
            <Outlet />
        </Protected>
    ),
    children: [
        {
            index: true,
            async lazy() {
                const { LeadRecyclingPage } = await import("@/pages/tools/lead_recycling");
                return { Component: LeadRecyclingPage };
            }
        },
        {
            path: "search",
            action: async ({ request }) => {
                const data = (await request.json()) as Record<string, unknown>;
                try {
                    const result = await DataService.client
                        .url("/tools/lead-recycling/search/")
                        .post(data)
                        .json<Record<string, unknown>>();
                    // Echo the request body so the UI can pair results with the exact
                    // search_parameters that produced them (avoids stale-payload races).
                    return handle_success({ ...result, search_parameters: data });
                } catch (e) {
                    return handle_error(e);
                }
            }
        },
        {
            path: "launch",
            action: async ({ request }) => {
                const data = await request.json();
                try {
                    return handle_success(
                        await DataService.client.url("/tools/lead-recycling/launch/").post(data).json()
                    );
                } catch (e) {
                    return handle_error(e);
                }
            }
        },
        {
            path: "quota",
            loader: async ({ request }) => {
                const url = new URL(request.url);
                const recyclingDate = url.searchParams.get("recycling_date");
                if (!recyclingDate) {
                    return json({ success: false, message: "recycling_date is required" });
                }
                try {
                    return handle_success(
                        await DataService.client
                            .url(`/tools/lead-recycling/quota/?recycling_date=${encodeURIComponent(recyclingDate)}`)
                            .get()
                            .json()
                    );
                } catch (e) {
                    return handle_error(e);
                }
            }
        },
        {
            path: "picklists",
            children: [
                {
                    path: "campaigns",
                    loader: async () => {
                        const campaigns = (await DataService.client
                            .url("/crm/app/picklists/campaigns/")
                            .get()
                            .json()) as CampaignPicklistOption[];
                        return campaigns;
                    }
                },
                {
                    path: "wrapups",
                    loader: async ({ request }) => {
                        const url = new URL(request.url);
                        const campaignId = url.searchParams.get("campaign_id");
                        if (!campaignId) {
                            return [];
                        }
                        const wrapups = (await DataService.client
                            .url(`/tools/lead-recycling/wrapups/?campaign_id=${encodeURIComponent(campaignId)}`)
                            .get()
                            .json()) as WrapupApiItem[];
                        return wrapups.map(wrapup => ({
                            value: String(wrapup.id),
                            label: wrapup.name
                        }));
                    }
                }
            ]
        }
    ]
};

export default LeadRecyclingRoute;
